import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LocalWhiteboardSettingChange, WhiteboardState } from 'features/whiteboard/types';
import { applyLocalWhiteboardSettings } from 'features/whiteboard/utils/applyLocalWhiteboardSettings';
import { RootState } from 'store/store';
import { WhiteboardSettingKey, WhiteboardSettings } from 'utils/whiteboard/settings';

export const initialState: WhiteboardState = {
  showLoader: false,
  uploadProgress: 0,
  uploadsInFlight: 0,
  whiteboardId: null,
  settings: {},
  localSettings: {},
};

const whiteboardSlice = createSlice({
  name: 'whiteboard',
  initialState,
  reducers: {
    // Counted rather than a flag: several images dropped at once upload in parallel, and the
    // first one to finish would otherwise hide the bar while the rest are still going.
    uploadStarted(state) {
      state.uploadsInFlight += 1;
    },
    uploadProgressChanged(state, action: PayloadAction<number>) {
      state.uploadProgress = action.payload;
    },
    uploadFinished(state) {
      state.uploadsInFlight = Math.max(0, state.uploadsInFlight - 1);

      if (state.uploadsInFlight === 0) {
        state.uploadProgress = 0;
      }
    },
    loaderDisplayChanged(state, action: PayloadAction<boolean>) {
      state.showLoader = action.payload;
    },
    whiteboardIdUpdated(state, action: PayloadAction<string>) {
      state.whiteboardId = action.payload;
    },
    whiteboardSettingsReceived(state, action: PayloadAction<WhiteboardSettings>) {
      Object.assign(state.settings, action.payload);
    },
    whiteboardSettingsPushed(state, action: PayloadAction<WhiteboardSettings>) {
      Object.assign(state.settings, action.payload);

      const local = state.whiteboardId ? state.localSettings[state.whiteboardId] : undefined;
      if (!local) {
        return;
      }

      (Object.keys(action.payload) as WhiteboardSettingKey[]).forEach((key) => {
        delete local[key];
      });
    },
    whiteboardLocalSettingChanged(state, action: PayloadAction<LocalWhiteboardSettingChange>) {
      if (!state.whiteboardId) {
        return;
      }

      const { key, value } = action.payload;
      const local = state.localSettings[state.whiteboardId] ?? {};

      local[key] = { value, syncedValue: state.settings[key] };
      state.localSettings[state.whiteboardId] = local;
    },
    whiteboardSettingsRemoved(state, action: PayloadAction<WhiteboardSettingKey[]>) {
      action.payload.forEach((key) => {
        delete state.settings[key];
      });
    },
    whiteboardSettingsReset(state) {
      state.settings = {};
    },
  },
});

export const {
  uploadStarted,
  uploadProgressChanged,
  uploadFinished,
  loaderDisplayChanged,
  whiteboardIdUpdated,
  whiteboardLocalSettingChanged,
  whiteboardSettingsPushed,
  whiteboardSettingsReceived,
  whiteboardSettingsRemoved,
  whiteboardSettingsReset,
} = whiteboardSlice.actions;

export const selectWhiteboardOpen = (state: RootState) =>
  state.layout.content.gridPanel === 'whiteboard';

export const selectGridPanelOpen = (state: RootState) => !!state.layout.content.gridPanel;

export const selectUploadProgress = (state: RootState) => state.whiteboard.uploadProgress;

export const selectIsUploading = (state: RootState) => state.whiteboard.uploadsInFlight > 0;

export const selectShowWhiteboardLoader = (state: RootState) => state.whiteboard.showLoader;

export const selectWhiteboardId = (state: RootState) => state.whiteboard.whiteboardId;

const selectWhiteboardSettings = (state: RootState) => state.whiteboard.settings;

const selectLocalWhiteboardSettings = (state: RootState) => {
  const { whiteboardId, localSettings } = state.whiteboard;

  return whiteboardId ? localSettings[whiteboardId] : undefined;
};

const selectEffectiveWhiteboardSettings = createSelector(
  [selectWhiteboardSettings, selectLocalWhiteboardSettings],
  applyLocalWhiteboardSettings
);

export const selectEffectiveWhiteboardMode = (state: RootState) =>
  selectEffectiveWhiteboardSettings(state).mode;

export default whiteboardSlice.reducer;
